import { Icon } from '@goodlok/react-sdk'
import {
	formatRGBChannelsForCSS,
	hexToRGBChannels,
} from '@goodlok/react-sdk/src/misc/hexToRGBChannels'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { Collapsible } from 'collapsible-react-component'
import { FunctionComponent, useMemo, useState } from 'react'
import { useRoleIds } from '../app/utils/useRoleIds'
import { api } from '../utilities/api'
import { AdminPageData, useAdminPageData } from '../utilities/useAdminPageData'
import { AdminAddToShift } from './AdminAddToShift'
import { AdminAdjustShiftSlots } from './AdminAdjustShiftSlots'
import styles from './AdminShiftTileSegment.module.sass'
import { AdminShiftTileSegmentPart } from './AdminShiftTileSegmentPart'

export const getStaffName = (
	staff: NonNullable<
		NonNullable<AdminPageData['getVenue']>['employeeRoles'][number]['role']
	>['shiftsProfiles'][number]['staff']
) => {
	const nameInReverseOrder = (staff?.user?.name ?? '').split(' ').reverse().join(' ')
	return nameInReverseOrder || staff?.user?.email || ''
}

type AdminShiftTileSegmentProps = {
	position?: NonNullable<
		NonNullable<ReturnType<typeof useAdminPageData>['data']>['getVenue']
	>['timetableDays'][number]['shiftGroups'][number]
	employeeRole?: NonNullable<AdminPageData['getVenue']>['employeeRoles'][number]
	date: string
	dayId: string
	setChange: (changed: boolean) => void
	updateData: () => Promise<void>
	isOnlyPositionOfGivenRoleForTheDay: boolean
	unassignReasons: NonNullable<AdminPageData['getVenue']>['enabledUnassignReasons']
}

export const AdminShiftTileSegment: FunctionComponent<AdminShiftTileSegmentProps> = ({
	position,
	employeeRole,
	date,
	dayId,
	setChange,
	updateData,
	isOnlyPositionOfGivenRoleForTheDay,
	unassignReasons,
}) => {
	let slotsTaken = 0
	let slotsTotal = 0
	position?.shifts.forEach((shift) => {
		slotsTotal++
		shift.queueItem?.id && shift.queueItem.unassignInfo === null ? slotsTaken++ : slotsTaken
	})
	const [slotsTotalState, setSlotsTotalState] = useState(slotsTotal)
	const [roles] = useRoleIds()
	const [editShiftSlots, setEditShiftSlots] = useState(false)
	const [addEmployeeActive, setAddEmployeeActive] = useState(false)

	const assignableEmployees = useMemo(() => {
		return (employeeRole?.role?.shiftsProfiles ?? [])
			.filter((shiftsProfile) => Boolean(shiftsProfile.staff))
			.map((shiftsProfile) => shiftsProfile.staff)
			.sort((staffA, staffB) => {
				return getStaffName(staffA)
					.toLocaleLowerCase()
					.localeCompare(getStaffName(staffB).toLocaleLowerCase(), 'cs')
			})
	}, [employeeRole?.role?.shiftsProfiles])

	const assignedShifts = position?.queue?.items.filter((queueItem) => {
		return queueItem.unassignInfo === null
	})
	const unassignedShifts = position?.queue?.items.filter((queueItem) => {
		return queueItem.unassignInfo !== null
	})
	const queryClient = useQueryClient()
	const unassignAndRemoveFromQueueMutation = api.shifts.unassignAndRemoveFromQueue.useMutation({
		onSuccess: () => {
			queryClient.invalidateQueries(['adminPageData'])
		},
	})

	const isFreeSlot = !(slotsTaken === slotsTotalState && assignableEmployees.length > 0)

	return (
		<div
			key={position?.id}
			className={styles.tile}
			style={{
				'--AdminShiftTileSegment-role-color': formatRGBChannelsForCSS(
					hexToRGBChannels(position?.type?.color || '#000000')
				),
			}}>
			<div className={clsx(!(slotsTotal - slotsTaken) && styles.roleFull, styles.role)}>
				<div className={clsx(!editShiftSlots && styles.roleHide)}>
					<AdminAdjustShiftSlots
						slotsTaken={slotsTaken}
						slotsTotalState={slotsTotalState}
						exit={setEditShiftSlots}
						shiftGroupId={position?.id}
						setSlotsTotalState={setSlotsTotalState}
					/>
				</div>
				<div className={styles.header}>
					<div className={styles.headerTime}>
						{!editShiftSlots && `${(roles ?? []).length > 1 ? position?.type?.name : ''}`}
						{!position?.position?.isDynamic &&
							!editShiftSlots &&
							!isOnlyPositionOfGivenRoleForTheDay &&
							` (${position?.position?.start}-${position?.position?.end})`}
					</div>
					<button
						className={styles.nameCounter}
						type="button"
						onClick={() => setEditShiftSlots(!editShiftSlots)}>
						{slotsTotalState - slotsTaken > 0 &&
							!editShiftSlots &&
							`${slotsTaken}/${slotsTotalState}`}
						<div
							className={clsx(
								!editShiftSlots && styles.roleFullTickWrapper,
								slotsTotalState - slotsTaken && styles.roleHide
							)}>
							<div
								className={clsx(
									slotsTotalState - slotsTaken === 0 && !editShiftSlots && styles.roleFullTick
								)}></div>
						</div>
					</button>
				</div>
			</div>
			<div className={clsx(styles.assignButtonWrapper)}>
				<div className={styles.employeeWrapper}>
					{assignedShifts?.map((item) => {
						const assigned = Boolean(item.shift)
						const confirmed = item.shift && item.shift.locked
						const swappedProfile = position?.shifts.find(
							(shift) =>
								shift.shiftsProfile?.id === item.shiftsProfile?.id &&
								shift.swapInfo &&
								shift.swapInfo.swappedShiftsProfile?.id
						)?.swapInfo
						return (
							<AdminShiftTileSegmentPart
								key={item.id}
								assigned={assigned}
								confirmed={confirmed}
								addEmployeeActive={addEmployeeActive}
								editShiftSlots={editShiftSlots}
								item={item}
								position={position}
								updateData={updateData}
								setChange={setChange}
								swappedProfile={swappedProfile}
								dayId={dayId}
								unassignReasons={unassignReasons}
							/>
						)
					})}
					{unassignedShifts && unassignedShifts.length > 0 && (
						<div className={styles.unassignedShifts}>
							{unassignedShifts?.map((shift) => (
								<div className={styles.unassignedShift} key={shift.id}>
									{getStaffName(shift.shiftsProfile?.staff)} (
									{shift.unassignInfo?.reason?.name ?? 'Neznámý důvod'})
									<button
										type="button"
										className={styles.crossIcon}
										onClick={() => {
											if (!shift.shiftsProfile || !position) {
												return
											}
											unassignAndRemoveFromQueueMutation.mutate({
												employeeId: shift.shiftsProfile.id,
												shiftGroupId: position.id,
											})
										}}>
										<Icon name="cross" />
									</button>
								</div>
							))}
						</div>
					)}
				</div>
				{isFreeSlot && (
					<div className={clsx(isFreeSlot ? styles.addEmployeeWrapper : styles.hide)}>
						<Collapsible open={!addEmployeeActive} revealType="topFirst">
							<button
								type="button"
								className={styles.addEmployee}
								onClick={() => setAddEmployeeActive(!addEmployeeActive)}>
								<Icon name="circlePlus" />
							</button>
						</Collapsible>
						<Collapsible open={addEmployeeActive} revealType="bottomFirst">
							<AdminAddToShift
								date={date}
								assignableEmployees={assignableEmployees}
								position={position}
								setAddEmployeeActive={setAddEmployeeActive}
								updateData={updateData}
							/>
						</Collapsible>
					</div>
				)}
			</div>
		</div>
	)
}
